import type { LucideIconName } from 'components/LucideIcon'
import type { LibraSearchParams } from 'routes/libra/-searchParams'

/** Toggle value for the list view; board views use their board id. */
export const LIST_VIEW_ID = 'list'

/**
 * Named tones are antd Tag's status presets, passed to its `color` as-is.
 * `{ color }` is for colors that come from config, e.g. lifecycle stages.
 */
export type PillTone =
  | 'default'
  | 'processing'
  | 'success'
  | 'warning'
  | 'error'
  | { color: string }

/** A Libra ticket to open. Route-typed rather than a raw URL, so links stay in-app. */
export interface LinkView {
  label: string
  to: '/libra'
  search: LibraSearchParams
}

/** One row in a pill's modal list, e.g. a comment thread. */
export interface ItemView {
  id: string
  title: string
  link?: LinkView
}

/** Longer explanation of a pill, listed in the modal. */
export interface PillInfo {
  /** Heading in the modal; defaults to the category label. */
  title?: string
  /** What this particular value means; shown above `description`. */
  valueDescription?: string
  description?: string
  items?: ReadonlyArray<ItemView>
}

export interface Pill {
  category: string
  /** Stable id used for filtering and board columns. */
  value: string
  label: string
  tooltip?: string
  tone?: PillTone
  icon?: LucideIconName
  info?: PillInfo
}

export interface TicketView {
  id: string
  title: string
  subtitle?: string
  pills: ReadonlyArray<Pill>
  /**
   * `inReview`: every step is done but the ticket isn't in a final stage yet.
   * `tone` colours the ring, e.g. the stage pill's tone so the two match.
   */
  progress?: { percent: number; inReview?: boolean; tone?: PillTone }
  link?: LinkView
  /** Only ever set on a chain's headline ticket. */
  canCreateVersion?: boolean
}

/**
 * One ticket chain: an asset's versions of one workflow. An asset with several
 * workflows gets one card per workflow.
 */
export interface CardView {
  id: string
  /** The asset's name; searched by the search box. */
  title: string
  subtitle?: string
  /** Asset-level pills (e.g. eligibility), repeated on every card for the asset. */
  pills: ReadonlyArray<Pill>
  /** Versions, newest first. Only the first (the headline) is matched by filters and boards. */
  tickets: readonly [TicketView, ...TicketView[]]
}

export interface PillOption {
  value: string
  label: string
  tone?: PillTone
}

/** Joins several categories' pills into one group on cards, e.g. every sign-off slot. Display only. */
export interface PillGroup {
  id: string
  label: string
  /** Shown once, on a neutral cap leading the group. */
  icon?: LucideIconName
}

export interface PillCategory {
  label: string
  /** Shown once, on a neutral cap leading the category's joined pill group. */
  icon?: LucideIconName
  group?: PillGroup
  /** Gives the category a filter in the filter menu. */
  filter?: boolean
  /** No pills anywhere; the value shows only as a row in the ticket modal's details. */
  showOnlyOnModal?: boolean
  /** Known values in display order. Values seen in the data are appended after. */
  options?: ReadonlyArray<PillOption>
}

export type PillCategories = Record<string, PillCategory>

/** Selected values per category. Categories must all match; values within a category are alternatives. */
export type CategorySelection = Partial<Record<string, ReadonlyArray<string>>>

export interface BoardColumnConfig {
  id: string
  label: string
  tone?: PillTone
  /** Cards whose pills match this selection, using the same rules as filters. */
  match: CategorySelection
}

export interface BoardConfig {
  /** Also the toggle value, so it must not be `LIST_VIEW_ID`. */
  id: string
  label: string
  columns: ReadonlyArray<BoardColumnConfig>
  /** When set, a last column collects cards that match no other column. */
  otherColumn?: { label: string }
}

export interface TicketDashboardConfig {
  /** Namespaces the remembered view choice. */
  id: string
  /** Every category the pills use. */
  categories: PillCategories
  boards: ReadonlyArray<BoardConfig>
}

export interface TicketFilterState {
  query: string
  selection: CategorySelection
}
